<template>
    <!-- <Breadcrumbs main="Setting" mains="Vendor Management" title="Vendor Management" /> -->
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5>Vendor List</h5>
                    <button class="btn btn-primary" @click="openModal()">
                        <i class="fa fa-plus"></i> Add Vendor
                    </button>
                </div>
            </div>
            <div class="card-body">
                <DataTable
                    :headers="headers"
                    :items="vendors"
                    :loading="loading"
                    :total="total"
                    :params="params"
                    @update:params="updateParams"
                >
                    <template #item.actions="{ item }">
                        <button class="btn btn-sm btn-warning me-2" @click="editVendor(item as Vendor)">
                            <i class="fa fa-edit"></i>
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deleteVendor(item as Vendor)">
                            <i class="fa fa-trash"></i>
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <div class="modal fade bd-example-modal-lg" id="vendorModal" tabindex="-1" ref="modalRef" aria-labelledby="vendorModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <h3 class="modal-header justify-content-center border-0">{{ modalTitle }}</h3>
                <div class="modal-body">
                    <div class="row">
                        <div class="col-md-6 mb-2">
                            <label class="form-label col-form-label">Code</label>
                            <input v-model="form.code" type="text" class="form-control" required placeholder="Enter vendor code">
                        </div>
                        <div class="col-md-6 mb-2">
                            <label class="form-label col-form-label">Name</label>
                            <input v-model="form.name" type="text" class="form-control" required placeholder="Enter vendor name">
                        </div>
                        <div class="col-md-6 mb-2">
                            <label class="form-label col-form-label">Contact Person</label>
                            <input v-model="form.contact_person" type="text" class="form-control" placeholder="Enter contact person">
                        </div>
                        <div class="col-md-6 mb-2">
                            <label class="form-label col-form-label">Contact Phone</label>
                            <input v-model="form.contact_phone" type="text" class="form-control" placeholder="Enter contact phone">
                        </div>
                        <div class="col-md-12 mb-2">
                            <label class="form-label col-form-label">Address</label>
                            <input v-model="form.address" type="text" class="form-control" placeholder="Enter address">
                        </div>
                        <div class="row">
                            <div class="col-md-4 mb-2">
                                <label class="form-label col-form-label">Province</label>
                                <select v-model="form.province" class="form-select">
                                    <option :value="null" disabled>Select Province</option>
                                    <option v-for="option in provinceOptions" :key="option.province" :value="option.province">
                                        {{ option.province }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-md-4 mb-2">
                                <label class="form-label col-form-label">City</label>
                                <select v-model="form.city" class="form-select">
                                    <option :value="null" disabled>Select City</option>
                                    <option v-for="option in cityOptions" :key="option.city" :value="option.city">
                                        {{ option.city }}
                                    </option>
                                </select>
                            </div>
                            <div class="col-md-4 mb-2">
                                <label class="form-label col-form-label">Area</label>
                                <select v-model="form.area" class="form-select">
                                    <option :value="null" disabled>Select Area</option>
                                    <option v-for="option in areaOptions" :key="option.area" :value="option.area">
                                        {{ option.area }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="btn btn-danger" type="button" @click="closeModal">Cancel</button>
                    <button class="btn btn-primary" type="submit" @click="saveVendor">Save</button>
                </div>
            </div>
        </div>
    </div>

</template>

<script lang="ts" setup>
import { ref, reactive, onMounted, watch, nextTick } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/Datatable.vue'
import { Modal } from 'bootstrap'
const provinceOptions = ref<{ province: string, city:string, area: string }[]>([])
const cityOptions = ref<{ province: string, city:string, area: string }[]>([])
const areaOptions = ref<{ province: string, city:string, area: string }[]>([])
const isEditing = ref(false) // <-- Add this line

// {{ Define Vendor interface }}
interface Vendor {
    ID: any; // Allow null for new vendors
    code: string;
    name: string;
    address: string;
    contact_person: string;
    contact_phone: string;
    province: any;
    city: any;
    area: any;
}

// {{ Define Params interface }}
interface DataTableParams {
    page: number;
    size: number;
    search: string;
}

const headers = [
    { text: 'Code', value: 'code' },
    { text: 'Name', value: 'name' },
    { text: 'Contact Person', value: 'contact_person' },
    { text: 'Contact Telp', value: 'contact_phone' },
    { text: 'City', value: 'city' },
    { text: 'Address', value: 'address' },
    { text: 'Actions', value: 'actions' }
]

// {{ Apply types to refs and reactive objects }}
const vendors = ref<Vendor[]>([])
const loading = ref(false)
const total = ref(0)
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: ''
})

const modalTitle = ref('')
const form = reactive<Vendor>({
    ID: null,
    code: '',
    name: '',
    contact_person: '',
    contact_phone: '',
    address: '',
    province: null,
    city: null,
    area: null
})

// Add this data data array
const dataVendors: Vendor[] = []
import * as master from "@/api/services/MasterRequest";

const fetchProvince = async () => {
    loading.value = true
    try {
        // Simulate API delay
        const resp = await master.postRequest("/destination/province", {page:1, size:10000,search:''});
        provinceOptions.value = resp.data;
    } catch (error) {
        toast.error('Failed to fetch vendors')
    }
    loading.value = false
}

const fetchCity = async (province: string) => {
    loading.value = true
    try {
        const resp = await master.postRequest("/destination/city", {
            page: 1,
            size: 10000,
            search: province
        });
        cityOptions.value = resp.data;
    } catch (error) {
        toast.error('Failed to fetch cities')
    }
    loading.value = false
}

const fetchArea = async (city: string) => {
    loading.value = true
    try {
        const resp = await master.postRequest("/destination/area", {
            page: 1,
            size: 10000,
            search: city
        });
        areaOptions.value = resp.data;
    } catch (error) {
        toast.error('Failed to fetch cities')
    }
    loading.value = false
}

const fetchVendors = async () => {
    loading.value = true
    try {
        // Simulate API delay
        const resp = await master.postRequest("/vendor/list", {...params});
        vendors.value = resp.data
        total.value = resp.total

    } catch (error) {
        toast.error('Failed to fetch vendors')
    }
    loading.value = false
}

// {{ Add type annotation for newParams }}
const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchVendors()
}

const modalRef = ref<HTMLElement | null>(null) // Type the ref
let bsModal: any | null = null // Type the bootstrap modal instance as any since Modal namespace can't be used directly

// View modal state
const viewModalRef = ref<HTMLElement | null>(null)
let bsViewModal: any | null = null
const selectedVendor = ref<Vendor | null>(null)

onMounted(() => {
    if (modalRef.value) { // Check if modalRef exists
        bsModal = new Modal(modalRef.value)
    }
    if (viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
    fetchProvince()
})

const closeModal = () => {
    if (bsModal) { // Check if bsModal is initialized
        bsModal.hide()
    }
}

const saveVendor = async () => {
    loading.value = true; // Indicate loading state
    try {
        // Simulate API call
        // await new Promise(resolve => setTimeout(resolve, 500));
        if (form.ID) {
            // Update existing vendor in data data
            await master.postRequest("/vendor/update", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Vendor updated successfully');
                    closeModal();
                    fetchVendors(); // Re-fetch to update the table view
                } else {
                    toast.error('Failed to update vendor');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to update vendor');
            })
            // const index = dataVendors.findIndex(r => r.ID === form.ID);
            // if (index !== -1) {
            //     dataVendors[index] = { ...form }; // Update with new form data
            // }
        } else {
            // Add new vendor to data data
            await master.postRequest("/vendor/create", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Vendor created successfully');
                    closeModal();
                    fetchVendors(); // Re-fetch to update the table view
                } else {
                    toast.error('Failed to create vendor');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to create vendor');
            })
        }
    } catch (error: any) { // Type error
        toast.error(error.message || 'Failed to save vendor');
    } finally {
        loading.value = false; // End loading state
    }
}

// {{ Define editVendor function }}
const editVendor = (vendor: Vendor) => {
    openModal(vendor); // Call openModal with the vendor data
}


// {{ Add type annotation for vendor parameter }}
const deleteVendor = async (vendor: Vendor) => {
    if (!confirm('Are you sure you want to delete this vendor?')) return

    loading.value = true; // Indicate loading state
    try {
        // Simulate API call
        await master.postRequest("/vendor/delete", {ID: vendor.ID}).then((resp) => {
            if (resp.status === 200) {
                toast.success('Vendor deleted successfully');
                closeModal();
                fetchVendors(); // Re-fetch to update the table view
            } else {
                toast.error('Failed to delete vendor');
            }
        })

    } catch (error: any) { // Type error
        toast.error(error.message || 'Failed to delete vendor');
    } finally {
        loading.value = false; // End loading state
    }
}

// Update openModal to ensure accounts array exists
const openModal = async (vendor: Vendor | null = null) => {
    if (vendor) {
        modalTitle.value = 'Edit Vendor'
        isEditing.value = true; // Stop watchers

        Object.assign(form, vendor);

        // Set Province (string value)
        if (vendor.province && provinceOptions.value.length > 0) {
            form.province = provinceOptions.value.find(opt => opt.province === vendor.province)?.province || null;
            await fetchCity(form.province); // Fetch city dat
        } else {
            form.province = null;
        }

        // Set City (string value)
        if (vendor.city && cityOptions.value.length > 0) {
            form.city = cityOptions.value.find(opt => opt.city === vendor.city)?.city || null;
            await fetchArea(form.city); // Fetch city data
        } else {
            form.city = null;
        }

        // Set Area (string value)
        if (vendor.area && areaOptions.value.length > 0) {
            form.area = areaOptions.value.find(opt => opt.area === vendor.area)?.area || null;
        } else {
            form.area = null;
        }

        isEditing.value = false; // Re-enable watchers after assignments
    } else {
        modalTitle.value = 'Create Vendor'
        form.ID = null
        form.code= '';
        form.name= '';
        form.address= '';
        form.province= null;
        form.city= null;
        form.area= null;
    }
    if (bsModal) {
        bsModal.show()
    }
}

// Watch for province changes to fetch cities
watch(() => form.province, async (newProvince) => {
    if (isEditing.value) return; // Skip watcher logic during editing
    if (newProvince && newProvince) {
        await fetchCity(newProvince)
        form.city = null // reset city selection
        areaOptions.value = []
        form.area = null
    } else {
        cityOptions.value = []
        form.city = null
        areaOptions.value = []
        form.area = null
    }
})

// Watch for city changes to fetch areas
watch(() => form.city, async (newCity) => {
    if (isEditing.value) return; // Skip watcher logic during editing
    if (newCity && newCity) {
        await fetchArea(newCity)
        form.area = null // reset area selection
    } else {
        areaOptions.value = []
        form.area = null
    }
})
</script>